import {
  AssociatedVehicle,
  AssociatedVehicleTimelineEvents,
  AssociatedVehicleTimelineRequest,
  CountResponse,
  EntityAttribute,
  ListAttributesRequest,
  ListEventsRequest,
  VehicleAgeTimeline,
} from "shared/api/api";
import { DEFAULT_ATTRIBUTES_SWR_CONFIG } from "shared/api/constants";
import { useAPI, useLoadMorePages, usePaginatedAPI } from "shared/api/hooks";
import { CountRequestWithVehiclesFilter } from "shared/api/vehicles/api";

import {
  Claim,
  ClaimAssociatedSignalEvent,
  ClaimAssociatedSignalEventsRequest,
  ClaimAssociatedVehiclesRequest,
  ClaimFiltersByVehicleAgeRequest,
  ClaimSmartGroup,
  ClaimsRunChartRequest,
  ClaimsRunChartResponse,
  ClaimsTopContributor,
  ClaimsTopContributorsRequest,
  countAssociatedVehiclesRequestURI,
  GetClaimRequest,
  getClaimRequestURI,
  getClaimsByVehicleAgeTimelineRequestURI,
  getClaimsRunChartRequestURI,
  getClaimsTopContributorsRequestURI,
  listAssociatedSignalEventsRequestURI,
  listAssociatedVehiclesRequestURI,
  listAssociatedVehiclesTimelineRequestURI,
  listClaimAttributesRequestURI,
  listClaimsCountRequestURI,
  listClaimsRequestURI,
  ListClaimsSmartGroupsRequest,
  listClaimsSmartGroupsRequestURI,
  matchClaimsRequestURI,
  MatchClaimsResponse,
  MatchEventsRequest,
  matchSignalEventsOccurrencesRequestURI,
  MatchSignalEventsOccurrencesResponse,
} from "./api";

export const useListClaims = (args: ListEventsRequest) =>
  usePaginatedAPI<typeof args, Claim[]>(listClaimsRequestURI, args);

export const useListClaimsCount = (args: CountRequestWithVehiclesFilter) =>
  usePaginatedAPI<typeof args, CountResponse>(listClaimsCountRequestURI, args);

export const useGetClaim = (args: GetClaimRequest) =>
  useAPI<typeof args, Claim>(getClaimRequestURI, args);

export const useClaimsRunChart = (args: ClaimsRunChartRequest) =>
  useAPI<typeof args, ClaimsRunChartResponse>(
    getClaimsRunChartRequestURI,
    args
  );

export const useClaimAttributes = (args: ListAttributesRequest) =>
  useAPI<typeof args, EntityAttribute[]>(
    listClaimAttributesRequestURI,
    args,
    DEFAULT_ATTRIBUTES_SWR_CONFIG
  );

export const useListClaimsTopContributors = (
  args: ClaimsTopContributorsRequest
) =>
  usePaginatedAPI<typeof args, ClaimsTopContributor[]>(
    getClaimsTopContributorsRequestURI,
    args
  );

export const useListClaimsByVehicleAgeTimeline = (
  args: ClaimFiltersByVehicleAgeRequest
) =>
  useAPI<typeof args, VehicleAgeTimeline[]>(
    getClaimsByVehicleAgeTimelineRequestURI,
    args
  );

export const useListClaimsAssociatedSignalEvents = (
  args: ClaimAssociatedSignalEventsRequest
) =>
  usePaginatedAPI<typeof args, ClaimAssociatedSignalEvent[]>(
    listAssociatedSignalEventsRequestURI,
    args
  );

export const useListClaimsAssociatedVehicles = (
  args: ClaimAssociatedVehiclesRequest
) =>
  usePaginatedAPI<typeof args, AssociatedVehicle[]>(
    listAssociatedVehiclesRequestURI,
    args
  );

export const useCountClaimsAssociatedVehicles = (
  args: ClaimAssociatedVehiclesRequest
) =>
  useAPI<typeof args, CountResponse>(countAssociatedVehiclesRequestURI, args);

export const useListClaimsAssociatedVehiclesTimeline = (
  args: AssociatedVehicleTimelineRequest
) =>
  useAPI<typeof args, AssociatedVehicleTimelineEvents[]>(
    listAssociatedVehiclesTimelineRequestURI,
    args
  );

export const useListClaimsSmartGroups = (
  args: ListClaimsSmartGroupsRequest,
  initialPageSize: number,
  loadMorePageSize: number
) =>
  useLoadMorePages<typeof args, ClaimSmartGroup>(
    listClaimsSmartGroupsRequestURI,
    args,
    initialPageSize,
    loadMorePageSize
  );

export const useMatchedSignalEventsOccurrences = (args: MatchEventsRequest) =>
  useAPI<typeof args, MatchSignalEventsOccurrencesResponse>(
    () => matchSignalEventsOccurrencesRequestURI(args.id, args),
    args
  );

export const useMatchedClaims = (args: MatchEventsRequest) =>
  useAPI<typeof args, MatchClaimsResponse>(
    () => matchClaimsRequestURI(args.id, args),
    args
  );
